/**
 * Facebook Login for Business — Embedded Signup loader + launcher for the
 * WhatsApp Cloud API (Meta BSP) multi-tenant onboarding.
 *
 * Loads the Meta JS SDK ON DEMAND (guarded — only when the WhatsApp connect
 * card actually launches the flow) and drives the Embedded Signup popup,
 * resolving with the `{code, waba_id, phone_number_id}` triple the backend
 * needs to exchange the OAuth code for a long-lived system-user token.
 *
 * NOTHING here is a secret: the app id + Embedded Signup config_id are PUBLIC
 * client identifiers, injected via `VITE_META_APP_ID` / `VITE_META_WABA_CONFIG_ID`.
 * When either is unset the caller renders a disabled state and never loads the
 * SDK — so the feature is dark unless it is fully provisioned.
 *
 * The `code` arrives via the `FB.login` callback; the `waba_id` +
 * `phone_number_id` arrive via a `message` event Meta posts from
 * facebook.com during the flow (`WA_EMBEDDED_SIGNUP`). We correlate the two and
 * resolve only when we have all three.
 */

const GRAPH_VERSION = 'v21.0';
const SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';
const SDK_SCRIPT_ID = 'facebook-jssdk';

export interface EmbeddedSignupResult {
  code: string;
  waba_id: string;
  phone_number_id?: string;
}

interface FbAuthResponse {
  code?: string;
}
interface FbLoginResponse {
  authResponse?: FbAuthResponse | null;
  status?: string;
}
interface FbSdk {
  init(params: Record<string, unknown>): void;
  login(cb: (response: FbLoginResponse) => void, params: Record<string, unknown>): void;
}

declare global {
  interface Window {
    FB?: FbSdk;
    fbAsyncInit?: () => void;
  }
}

function readEnv(value: string | undefined): string | undefined {
  const trimmed = value ? String(value).trim() : '';
  return trimmed ? trimmed : undefined;
}

/** Public Meta app id (`VITE_META_APP_ID`), or undefined when unset. */
export function getMetaAppId(): string | undefined {
  return readEnv(import.meta.env.VITE_META_APP_ID);
}

/** Public Embedded Signup config_id (`VITE_META_WABA_CONFIG_ID`), or undefined. */
export function getWabaConfigId(): string | undefined {
  return readEnv(import.meta.env.VITE_META_WABA_CONFIG_ID);
}

/** True only when BOTH public client identifiers are configured. */
export function isEmbeddedSignupConfigured(): boolean {
  return Boolean(getMetaAppId() && getWabaConfigId());
}

let sdkPromise: Promise<FbSdk> | null = null;
// The appId `window.FB` is CURRENTLY init'd with. Meta's JS SDK is a single
// global object — when the WhatsApp Embedded Signup card and the Instagram
// Direct card live on the same page but are backed by DIFFERENT Meta Apps
// (a real, supported topology — ADR-INST-001 leaves "App único vs. App
// separado" as configuration, not code), whichever card mounts/logs in FIRST
// wins the appId for the rest of the page's lifetime UNLESS we re-init on a
// mismatch. `FB.init()` is safe to call again (it just re-applies config on
// the same script instance); only the one-time script injection is guarded.
let initializedAppId: string | null = null;

/**
 * Loads the Meta JS SDK once (script injected a single time) and resolves
 * with the initialized `FB` object, re-running `FB.init()` whenever a caller
 * asks for a DIFFERENT `appId` than the one currently active — e.g. the
 * WhatsApp card (`VITE_META_APP_ID`) and the Instagram card
 * (`VITE_META_IG_APP_ID`) pointing at two distinct Meta Apps. Exported so
 * sibling Meta login flows (e.g. Instagram Direct — `instagramLogin.ts`)
 * share the SAME loader/script tag: two loaders could double-inject it.
 */
export function loadSdk(appId: string): Promise<FbSdk> {
  if (window.FB) {
    if (initializedAppId !== appId) {
      window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version: GRAPH_VERSION });
      initializedAppId = appId;
    }
    return Promise.resolve(window.FB);
  }
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise<FbSdk>((resolve, reject) => {
    window.fbAsyncInit = function fbAsyncInit() {
      try {
        window.FB!.init({
          appId,
          autoLogAppEvents: true,
          xfbml: false,
          version: GRAPH_VERSION,
        });
        initializedAppId = appId;
        resolve(window.FB!);
      } catch (err) {
        sdkPromise = null;
        reject(err);
      }
    };

    if (document.getElementById(SDK_SCRIPT_ID)) return; // script already injected

    const js = document.createElement('script');
    js.id = SDK_SCRIPT_ID;
    js.src = SDK_SRC;
    js.async = true;
    js.defer = true;
    js.crossOrigin = 'anonymous';
    js.onerror = () => {
      sdkPromise = null;
      reject(
        new Error('Falha ao carregar o SDK do Facebook. Verifique sua conexão e tente novamente.'),
      );
    };
    document.body.appendChild(js);
  });

  return sdkPromise;
}

/**
 * Launch the Embedded Signup popup and resolve with the exchange triple.
 * Rejects on cancel, error, or a missing field. Never throws synchronously
 * after the config check.
 */
export async function launchEmbeddedSignup(): Promise<EmbeddedSignupResult> {
  const appId = getMetaAppId();
  const configId = getWabaConfigId();
  if (!appId || !configId) {
    throw new Error(
      'WhatsApp Embedded Signup não está configurado (VITE_META_APP_ID / VITE_META_WABA_CONFIG_ID).',
    );
  }

  const FB = await loadSdk(appId);

  return new Promise<EmbeddedSignupResult>((resolve, reject) => {
    const sessionInfo: { waba_id?: string; phone_number_id?: string } = {};
    let settled = false;

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      fn();
    };

    const onMessage = (event: MessageEvent) => {
      if (
        event.origin !== 'https://www.facebook.com' &&
        event.origin !== 'https://web.facebook.com'
      ) {
        return;
      }
      let data: Record<string, unknown> | undefined;
      try {
        data =
          typeof event.data === 'string'
            ? JSON.parse(event.data)
            : event.data && typeof event.data === 'object'
              ? (event.data as Record<string, unknown>)
              : undefined;
      } catch {
        return; // non-JSON postMessage traffic is noise
      }
      if (!data || data.type !== 'WA_EMBEDDED_SIGNUP') return;

      const payload = data.data && typeof data.data === 'object'
        ? data.data as Record<string, unknown> : {};
      const eventType = data.event ?? payload.event;
      if (eventType === 'CANCEL') {
        finish(() => reject(new Error('Conexão do WhatsApp cancelada.')));
        return;
      }
      if (eventType === 'ERROR') {
        const errorMsg =
          payload.error_message || 'Erro durante o cadastro incorporado da Meta.';
        finish(() => reject(new Error(String(errorMsg))));
        return;
      }

      if (payload.waba_id) {
        sessionInfo.waba_id = String(payload.waba_id);
      }
      if (payload.phone_number_id) {
        sessionInfo.phone_number_id = String(payload.phone_number_id);
      }
    };

    window.addEventListener('message', onMessage);

    FB.login(
      (response: FbLoginResponse) => {
        const code = response?.authResponse?.code;
        if (!code) {
          finish(() => reject(new Error('Autorização não concluída. Tente novamente.')));
          return;
        }

        const tryResolve = () => {
          if (sessionInfo.waba_id) {
            finish(() =>
              resolve({
                code,
                waba_id: sessionInfo.waba_id as string,
                phone_number_id: sessionInfo.phone_number_id,
              }),
            );
            return true;
          }
          return false;
        };

        if (tryResolve()) return;

        // Tolerância de até 2s para evitar race condition caso o popup feche
        // frações de segundo antes da entrega do postMessage da janela.
        const start = Date.now();
        const interval = setInterval(() => {
          if (tryResolve() || Date.now() - start > 2000) {
            clearInterval(interval);
            if (!sessionInfo.waba_id) {
              finish(() =>
                reject(
                  new Error(
                    'Não recebemos a conta WhatsApp Business da Meta. Refaça o cadastro incorporado.',
                  ),
                ),
              );
            }
          }
        }, 100);
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: { sessionInfoVersion: '3' },
      },
    );
  });
}

/**
 * Abre o popup do Facebook Login para autorização OAuth e retorna o `code`.
 * Ao contrário do `launchEmbeddedSignup`, não exige que o usuário cadastre ou
 * selecione um NOVO número de telefone (útil para reconexão de WABA existente).
 */
export async function launchFbLoginForCode(): Promise<string> {
  const appId = getMetaAppId();
  const configId = getWabaConfigId();
  if (!appId || !configId) {
    throw new Error(
      'WhatsApp Embedded Signup não está configurado (VITE_META_APP_ID / VITE_META_WABA_CONFIG_ID).',
    );
  }

  const FB = await loadSdk(appId);

  return new Promise<string>((resolve, reject) => {
    FB.login(
      (response: FbLoginResponse) => {
        const code = response?.authResponse?.code;
        if (!code) {
          reject(new Error('Autorização cancelada ou não concluída pelo Facebook.'));
          return;
        }
        resolve(code);
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: { sessionInfoVersion: '3' },
      },
    );
  });
}
